import { Fragment, ReactNode } from "react";
import classNames from "classnames";
import styles from "./index.module.scss";

type PagerProps = {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Left-hand caption: "312 users · 50 per page". */
  summary?: ReactNode;
  /**
   * Prev/next button contents. Override on translated (learner-facing)
   * surfaces — the admin console keeps the English defaults per the
   * English-only-for-internal-tools policy.
   */
  prevLabel?: ReactNode;
  nextLabel?: ReactNode;
};

export default function Pager(props: PagerProps) {
  function goTo(page: number) {
    if (page >= 1 && page <= props.pageCount && page !== props.page) {
      props.onPageChange(page);
    }
  }

  return (
    <div className={classNames(styles.pager, "talaera-pager")}>
      {props.summary !== undefined && (
        <span className={styles.summary}>{props.summary}</span>
      )}
      <div className={styles.buttons}>
        <button
          type="button"
          className={styles.button}
          disabled={props.page <= 1}
          onClick={() => goTo(props.page - 1)}
        >
          {props.prevLabel ?? "‹ Prev"}
        </button>
        {pagerItems(props.page, props.pageCount).map((item, index) => (
          <Fragment key={index}>
            {item === "gap" ? (
              <span className={styles.gap}>…</span>
            ) : (
              <button
                type="button"
                className={classNames(styles.button, item === props.page && styles.on)}
                onClick={() => goTo(item)}
              >
                {item}
              </button>
            )}
          </Fragment>
        ))}
        <button
          type="button"
          className={styles.button}
          disabled={props.page >= props.pageCount}
          onClick={() => goTo(props.page + 1)}
        >
          {props.nextLabel ?? "Next ›"}
        </button>
      </div>
    </div>
  );
}

/** Page numbers to render: first, last, and a window around the current page. */
function pagerItems(page: number, pageCount: number): (number | "gap")[] {
  if (pageCount <= 7) {
    return Array.from({ length: Math.max(pageCount, 0) }, (_, i) => i + 1);
  }

  const wanted = [1, page - 1, page, page + 1, pageCount].filter(
    p => p >= 1 && p <= pageCount
  );
  const pages = [...new Set(wanted)].sort((a, b) => a - b);

  const items: (number | "gap")[] = [];
  pages.forEach((p, index) => {
    if (index > 0 && p - pages[index - 1] > 1) {
      items.push("gap");
    }
    items.push(p);
  });
  return items;
}
